import { useRef, useEffect, useState, useContext } from "react";
import LanguageSelector from "./ToolsMenuItems/LanguageSelector";
import { AnimatePresence, motion } from "framer-motion";
import ThemeSelector from "./ToolsMenuItems/ThemeSelector";
import NavBarDisplayModeSelector from "./ToolsMenuItems/NavBarDisplayModeSelector";
import Title from "./ToolsMenuItems/Title";
import { VisibilityModeCtx } from "../../../contexts/visibilityModeCtx";
import { useTheme } from "../../../../../hooks/useTheme";
import useSystemThemeListener from "../../../../../hooks/useSystemThemeListener";
import ForceSystemTheme from "./ToolsMenuItems/ForceSystemTheme";
import useForceSystemTheme from "../../../hooks/useForceSystemTheme";
import useCloseWhenClickedOutside from "../../../hooks/useCloseWhenClickedOutside";
import ToolsMenuButton from "./ToolsMenuButton";

export default function ToolsMenu({showHeader}: {showHeader: boolean}) {
	const { visibilityMode } = useContext(VisibilityModeCtx);
	const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
	const toolsMenuRef = useRef<HTMLDivElement>(null);

	const { theme, toggleTheme, applyTheme } = useTheme();
	const { forceSystemTheme, setForceSystemTheme } = useForceSystemTheme()
	useSystemThemeListener({ theme, applyTheme, forceSystemTheme });

	useEffect(() => {
		if (!showHeader) {
			setIsToolsMenuOpen(false);
		}
	}, [showHeader]);

	useCloseWhenClickedOutside({
		refs: [toolsMenuRef],
		setState: setIsToolsMenuOpen,
	});

	const toggleMenu = () => {
		setIsToolsMenuOpen(!isToolsMenuOpen);
	};

	return (
		<div className="relative" ref={toolsMenuRef}>
			<ToolsMenuButton
				toggleMenu={toggleMenu}
				visibilityMode={visibilityMode}
			/>

			<AnimatePresence>
				{isToolsMenuOpen && (
					<motion.div
						initial={{ opacity: 0, scale: 0 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className={`
                            absolute left-1/2 md:right-1/2 md:left-auto z-10 
                            flex justify-center items-start flex-col gap-3 sm:gap-4 lg:gap-6 2xl:gap-[2vw]
                            rounded-md md:rounded-xl xl:rounded-2xl 2xl:rounded-[1vw]
							py-5 lg:py-7 2xl:py-[3vw]
							px-10 lg:px-12 2xl:px-[4.5vw]
							mt-3 2xl:mt-[0.5vw]
                            origin-top-left md:origin-top-right
                            card-multi-section !shadow-md lg:shadow-lg xl:!shadow-[0_0.7vw_1.5vw_-0.3vw_rgba(0,0,0,0.1),_0_0.4vw_0.6vw_-0.4vw_rgba(0,0,0,0.1)]
							min-w-[250px] xs:w-[350px] lg:w-[450px] xl:w-[500px] 2xl:w-[42vw]
							font-size-from-base-to-2xl
                        `}	
						role="menu"
						aria-orientation="vertical"
						aria-labelledby="options-menu"
					>
						<Title />
						<LanguageSelector />
						<ThemeSelector 
							theme={theme} 
							toggleTheme={toggleTheme} 
						/>
						<ForceSystemTheme 
							forceSystemTheme={forceSystemTheme} 
							setForceSystemTheme={setForceSystemTheme} 
						/>
						<NavBarDisplayModeSelector/>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}